import { useState } from 'react'
import {
  Form,
  InputNumber,
  Row,
  Col,
  Radio,
  Select,
  Space,
  Tooltip,
  Card,
} from 'antd'
import { useTranslation } from 'react-i18next'
import type { IRelatedProduct } from '@/types/IOrder'
import Icon from '@components/common/Icon'

interface SliderItemProps {
  formName: string[]
  data: any
  parentName?: number
  currentProductData?: any
}

const useSliderLogic = (formName: string[], data: any, parentName?: number, currentProductData?: any) => {
  const form = Form.useFormInstance()
  const [selectedProductUuid, setSelectedProductUuid] = useState<string>('')
  const [cardCurrentValues, setCardCurrentValues] = useState<Record<string, number>>({})

  const formValues = form.getFieldsValue()

  const actualParentName = parentName ?? (Array.isArray(formName) ? formName[0] : 0)

  const watchedSupportLevel = Form.useWatch(['items', actualParentName, 'support_level'], form)
  const currentSupportLevel = formValues?.items?.[actualParentName]?.support_level ?? watchedSupportLevel

  let relatedProducts = []
  if (currentProductData?.product?.related_products) {
    relatedProducts = currentProductData.product.related_products
  } else if (currentProductData?.related_products) {
    relatedProducts = currentProductData.related_products
  } else {
    relatedProducts = formValues?.items?.[actualParentName]?.order_request?.related_products ??
      formValues?.items?.[actualParentName]?.product?.related_products ?? []
  }

  const filteredProducts = relatedProducts.filter((product: IRelatedProduct) => {
    const productSupportLevel = product.product?.pivot?.support_level ?? product.pivot?.support_level
    return productSupportLevel === currentSupportLevel
  })

  const uniqueProducts = filteredProducts.reduce((acc: any[], product: IRelatedProduct) => {
    const uuid = product.uuid ?? product.product?.uuid
    const name = product.name ?? product.product?.name
    if (uuid && !acc.some(p => p.value === uuid)) {
      acc.push({ value: uuid, label: name })
    }
    return acc
  }, [])

  const selectedProduct = filteredProducts.find((product: IRelatedProduct) => {
    const uuid = product.uuid ?? product.product?.uuid
    return uuid === selectedProductUuid
  })

  const getParentCurrent = () => data.current ?? 0
  const getTotalDistributed = () => Object.values(cardCurrentValues).reduce((sum, val) => sum + val, 0)
  const getRemainingValue = () => getParentCurrent() - getTotalDistributed()

  const handleProductChange = (value: string) => {
    setSelectedProductUuid(value)
    const product = filteredProducts.find((p: IRelatedProduct) => {
      const uuid = p.uuid ?? p.product?.uuid
      return uuid === value
    })

    if (product) {
      const maxCards = product.pivot?.max ?? product.product?.pivot?.max ?? 1
      const initialValues: Record<string, number> = {};
      for (let i = 1; i <= maxCards; i++) {
        initialValues[`${value}-${i}`] = 0
      }
      setCardCurrentValues(initialValues)

      const relatedProductFieldName = [...formName.slice(0, -1), 'related_product']
      form.setFieldValue(relatedProductFieldName, {
        uuid: value,
        support_level: currentSupportLevel,
        items: []
      })
    }
  }

  const handleCardCurrentChange = (cardIndex: number, value: number | null) => {
    if (!selectedProductUuid) return

    const newValue = value ?? 0
    const cardKey = `${selectedProductUuid}-${cardIndex}`
    const newValues = { ...cardCurrentValues, [cardKey]: newValue }
    const total = Object.values(newValues).reduce((sum, val) => sum + val, 0)

    if (total > getParentCurrent()) return

    setCardCurrentValues(newValues)

    const relatedProductFieldName = [...formName.slice(0, -1), 'related_product']
    const currentRelatedProduct = form.getFieldValue(relatedProductFieldName) ?? {}
    const currentItems = currentRelatedProduct.items ?? []

    const updatedItems = currentItems.map((item: any) =>
      item.index === cardIndex ? { ...item, current: newValue } : item
    )

    if (!updatedItems.some((item: any) => item.index === cardIndex)) {
      updatedItems.push({ index: cardIndex, current: newValue })
    }

    updatedItems.sort((a: any, b: any) => a.index - b.index)

    const supportLevelValue = selectedProduct?.pivot?.support_level ??
      selectedProduct?.product?.pivot?.support_level

    form.setFieldValue(relatedProductFieldName, {
      uuid: selectedProductUuid,
      support_level: supportLevelValue,
      items: updatedItems
    })
  }

  return {
    currentSupportLevel,
    filteredProducts,
    uniqueProducts,
    selectedProduct,
    selectedProductUuid,
    cardCurrentValues,
    getRemainingValue,
    handleProductChange,
    handleCardCurrentChange
  }
}

const ProductSelect = ({
  selectedProductUuid,
  uniqueProducts,
  handleProductChange,
  t
}: {
  selectedProductUuid: string
  uniqueProducts: { value: string; label: string }[]
  handleProductChange: (value: string) => void
  t: (key: string) => string
}) => (
  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
    <Col xs={24} sm={12} md={8}>
      <span style={{ color: 'red' }}>*</span>
      <Form.Item
        label={t('SELECT_RELATED_PRODUCT')}
        rules={[{ required: true, message: t('requiredItem') }]}
        initialValue={selectedProductUuid}
      >
        <Select
          value={selectedProductUuid}
          rules={[{ required: true, message: t('requiredItem') }]}
          onChange={handleProductChange}
          placeholder={t('SELECT_RELATED_PRODUCT')}
          data-cy="select-related-product"
          options={uniqueProducts}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>
    </Col>
  </Row>
)

const CardItem = ({
  cardIndex,
  formName,
  selectedProduct,
  selectedProductUuid,
  currentValue,
  remainingValue,
  handleCardCurrentChange,
  t
}: {
  cardIndex: number
  formName: string[]
  selectedProduct: IRelatedProduct
  selectedProductUuid: string
  currentValue: number
  remainingValue: number
  handleCardCurrentChange: (cardIndex: number, value: number | null) => void
  t: (key: string) => string
}) => (
  <Col xs={24} sm={12} lg={8}>
    <Card size="small" style={{ marginBottom: 12 }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <Form.Item
            name={[...formName.slice(0, -1), 'related_product', 'items', cardIndex - 1, 'index']}
            label={t('ITEM_INDEX')}
            rules={[{ required: true, message: t('requiredItem') }]}
            initialValue={cardIndex}
          >
            <Radio.Group optionType="button" buttonStyle="solid" value={cardIndex}>
              <Radio value={cardIndex}>{cardIndex}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name={[...formName.slice(0, -1), 'related_product', 'support_level']}
            label={t('SUPPORT_LEVEL')}
            rules={[{ required: true, message: t('requiredItem') }]}
            initialValue={selectedProduct?.pivot?.support_level ?? selectedProduct?.product?.pivot?.support_level}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={
                selectedProduct?.pivot?.support_level ??
                selectedProduct?.product?.pivot?.support_level
              }
            >
              <Radio
                value={
                  selectedProduct?.pivot?.support_level ??
                  selectedProduct?.product?.pivot?.support_level
                }
              >
                {selectedProduct?.pivot?.support_level ??
                  selectedProduct?.product?.pivot?.support_level}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </div>

        <Form.Item
          name={[...formName.slice(0, -1), 'related_product', 'uuid']}
          label={t('RELATED_PRODUCT')}
          rules={[{ required: true, message: t('requiredItem') }]}
          initialValue={selectedProductUuid}
        >
          <Radio.Group optionType="button" buttonStyle="solid" value={selectedProductUuid}>
            <Radio value={selectedProductUuid}>{selectedProduct?.name ??
              selectedProduct?.product?.name}</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name={[...formName.slice(0, -1), 'related_product', 'items', cardIndex - 1, 'current']}
          label={t('CURRENT_VALUE')}
          rules={[{ required: true, message: t('requiredItem') }]}
          initialValue={0}
        >
          <InputNumber
            min={0}
            max={remainingValue}
            value={currentValue}
            onChange={(value) => handleCardCurrentChange(cardIndex, value)}
            style={{ width: '100%' }}
            addonBefore={<span style={{ fontSize: '10px' }}>{t('Qtd')}</span>}
          />
        </Form.Item>
      </Space>
    </Card>
  </Col>
)

const SliderItem = ({ formName, data, parentName, currentProductData }: SliderItemProps) => {
  const { t } = useTranslation();
  const {
    filteredProducts,
    uniqueProducts,
    selectedProduct,
    selectedProductUuid,
    cardCurrentValues,
    getRemainingValue,
    handleProductChange,
    handleCardCurrentChange
  } = useSliderLogic(formName, data, parentName, currentProductData)


  const sliderInputNumber = () => (
    <Form.Item name={formName} rules={[{ required: true, message: t('requiredItem') }]}>
      <InputNumber
        addonBefore={
          <Space>
            <span style={{ color: 'red' }}>*</span>
            <Icon name={`fa-light ${data.item.icon_name}`} />
            <Tooltip title={data.item.description}>
              <span>{data.item.override_name ?? data.item.item}</span>
            </Tooltip>
          </Space>
        }
        addonAfter={data?.item.postfix}
        style={{ width: '100%' }}
      />
    </Form.Item>
  )

  if (!data.item.related_product || filteredProducts.length === 0) {
    return sliderInputNumber()
  }

  return (
    <>
      {sliderInputNumber()}

      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <ProductSelect
          selectedProductUuid={selectedProductUuid}
          uniqueProducts={uniqueProducts}
          handleProductChange={handleProductChange}
          t={t}
        />

        {selectedProduct && (
          <Row gutter={[12, 12]}>
            {Array.from({
              length: selectedProduct.pivot?.max ?? selectedProduct.product?.pivot?.max ?? 1
            }).map((_, i) => {
              const cardIndex = i + 1;
              const cardKey = `${selectedProductUuid}-${cardIndex}`;
              return (
                <CardItem
                  key={cardKey}
                  cardIndex={cardIndex}
                  formName={formName}
                  selectedProduct={selectedProduct}
                  selectedProductUuid={selectedProductUuid}
                  currentValue={cardCurrentValues[cardKey] ?? 0}
                  remainingValue={getRemainingValue() + (cardCurrentValues[cardKey] ?? 0)}
                  handleCardCurrentChange={handleCardCurrentChange}
                  t={t}
                />
              )
            })}
          </Row>
        )}
      </div>
    </>
  )
}

export default SliderItem
